/**
 * WAYP-945 — Dispatch Loads variant route helpers.
 *
 * Variant slug ↔ canonical key mapping mirrors the server's
 * `DISPATCH_VARIANT_SLUGS` (load.dto.ts L23-28). The frontend writes URL
 * paths using the slug; controllers and the SSRM datasource accept either
 * slug or canonical key in the `?variant=` query / `context.variant` body.
 *
 * Variant chip color tokens follow design-spec §3 row 3:
 *   Freight Dispatch → info (blue)
 *   Intermodal       → warning (amber)
 *   Livestock        → success (green)
 *   FTL Brokerage    → neutral (gray)
 */
export const DISPATCH_VARIANT_SLUGS = ['freight', 'intermodal', 'livestock', 'ftl-brokerage'] as const;
export type DispatchVariantSlug = (typeof DISPATCH_VARIANT_SLUGS)[number];

export const DISPATCH_VARIANT_KEYS = [
  'mod_dispatch_freight_main',
  'mod_intermodal_main',
  'mod_livestock_main',
  'mod_ftl_brokerage_main',
] as const;
export type DispatchVariantKey = (typeof DISPATCH_VARIANT_KEYS)[number];

export const SLUG_TO_KEY: Record<DispatchVariantSlug, DispatchVariantKey> = {
  freight: 'mod_dispatch_freight_main',
  intermodal: 'mod_intermodal_main',
  livestock: 'mod_livestock_main',
  'ftl-brokerage': 'mod_ftl_brokerage_main',
};

export const KEY_TO_SLUG: Record<DispatchVariantKey, DispatchVariantSlug> = {
  mod_dispatch_freight_main: 'freight',
  mod_intermodal_main: 'intermodal',
  mod_livestock_main: 'livestock',
  mod_ftl_brokerage_main: 'ftl-brokerage',
};

export const VARIANT_LABELS: Record<DispatchVariantKey, string> = {
  mod_dispatch_freight_main: 'Freight',
  mod_intermodal_main: 'Intermodal',
  mod_livestock_main: 'Livestock',
  mod_ftl_brokerage_main: 'FTL Brokerage',
};

/**
 * Per-variant H1 / page-shell titles for the loads list page.
 * Distinct from `VARIANT_LABELS` (which feeds the chip + breadcrumb leaf)
 * because operators wanted the page header to read like the sidebar entry
 * — "Dispatch", "Intermodal", "Livestock", "Full Truckload Brokerage" —
 * not the shorter chip label (Jorge directive 2026-05-19).
 */
export const VARIANT_PAGE_TITLES: Record<DispatchVariantKey, string> = {
  mod_dispatch_freight_main: 'Dispatch',
  mod_intermodal_main: 'Intermodal',
  mod_livestock_main: 'Livestock',
  mod_ftl_brokerage_main: 'Full Truckload Brokerage',
};

export const VARIANT_DEFAULT_LOAD_TYPES: Record<DispatchVariantKey, string> = {
  mod_dispatch_freight_main: 'general',
  mod_intermodal_main: 'intermodal',
  mod_livestock_main: 'livestock',
  mod_ftl_brokerage_main: 'ftl',
};

/**
 * Variant chip color token (design-spec §3 row 3). Maps to whatever the
 * `Badge` primitive supports; if a value is unknown to Badge it falls
 * back to the default styling.
 */
export type VariantChipTone = 'info' | 'warning' | 'success' | 'neutral';
export const VARIANT_CHIP_TONES: Record<DispatchVariantKey, VariantChipTone> = {
  mod_dispatch_freight_main: 'info',
  mod_intermodal_main: 'warning',
  mod_livestock_main: 'success',
  mod_ftl_brokerage_main: 'neutral',
};

/**
 * Resolve a URL slug or canonical key string to a canonical key.
 * Returns null for unknown values so the page can redirect / render
 * an empty state instead of crashing.
 */
export function resolveVariantKey(input: string | null | undefined): DispatchVariantKey | null {
  if (!input) return null;
  if ((DISPATCH_VARIANT_KEYS as readonly string[]).includes(input)) {
    return input as DispatchVariantKey;
  }
  if ((DISPATCH_VARIANT_SLUGS as readonly string[]).includes(input)) {
    return SLUG_TO_KEY[input as DispatchVariantSlug];
  }
  return null;
}

/**
 * URL helpers — keep all `/dispatch-loads/*` path string-construction in
 * one place so we don't sprinkle hard-coded strings.
 */
export const DISPATCH_LOADS_BASE = '/dispatch-loads';
export function dispatchLoadsListUrl(variant: DispatchVariantKey): string {
  return `${DISPATCH_LOADS_BASE}/${KEY_TO_SLUG[variant]}`;
}
export function dispatchLoadsNewUrl(variant: DispatchVariantKey): string {
  return `${DISPATCH_LOADS_BASE}/${KEY_TO_SLUG[variant]}/new`;
}
export function dispatchLoadsEditUrl(
  variant: DispatchVariantKey,
  id: string,
  /**
   * Optional split-cohort focus hint. When the operator opens a split
   * CHILD's edit URL, the form redirects to the master + appends
   * `?focusLoad=<childId>` so the BillingSection can activate the tab
   * that matches the load they originally clicked (V1 parity).
   */
  focusLoad?: string,
): string {
  const base = `${DISPATCH_LOADS_BASE}/${KEY_TO_SLUG[variant]}/${id}/edit`;
  return focusLoad ? `${base}?focusLoad=${encodeURIComponent(focusLoad)}` : base;
}
export function dispatchLoadsDetailUrl(variant: DispatchVariantKey, id: string): string {
  return `${DISPATCH_LOADS_BASE}/${KEY_TO_SLUG[variant]}/${id}`;
}
/**
 * 2026-05-20 — Per-variant Recurring Loads grid URL. Same per-variant
 * surface registered in App.tsx as
 * `/dispatch-loads/:variant/recurring`. Returned by the form's
 * post-save redirect when the operator ticks "Make Recurring" so they
 * land on the Recurring Loads grid (not the operational dispatch
 * grid) after saving a template.
 */
export function dispatchLoadsRecurringUrl(variant: DispatchVariantKey): string {
  return `${DISPATCH_LOADS_BASE}/${KEY_TO_SLUG[variant]}/recurring`;
}
/**
 * Per-variant Loads History grid URL — the surface registered in App.tsx
 * as `/dispatch-loads/history/:variant` (LoadsHistoryPage). Returned by
 * the form's Cancel / post-save redirect when the edit was opened FROM
 * the history grid (the edit link carries `?from=history`), so the
 * operator lands back on History instead of the operational grid.
 */
export function dispatchLoadsHistoryUrl(variant: DispatchVariantKey): string {
  return `${DISPATCH_LOADS_BASE}/history/${KEY_TO_SLUG[variant]}`;
}
/**
 * Dispatch Tenders grid URL — the surface registered in App.tsx as the
 * static `/dispatch-loads/tender` route (DispatchTendersPage). Returned by
 * the form's Cancel / post-save redirect when the edit was opened FROM the
 * Tenders grid (the edit link carries `?from=tenders`). Variant-agnostic:
 * the Tenders page carries its own tab strip, so there is no per-variant
 * tender URL.
 */
export function dispatchLoadsTenderUrl(): string {
  return `${DISPATCH_LOADS_BASE}/tender`;
}
